import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";
import { authenticatedDownload, smartSaveOrShare } from "@/lib/download";

/**
 * Document viewer modal — renders provided React content (HTML preview of the
 * receipt or certificate) inside the SAME document so it works in cross-site
 * iframes (Emergent preview) where Chrome blocks blob: PDFs.
 *
 * Props:
 *  - open: bool
 *  - title: string
 *  - content: ReactNode  (the styled preview)
 *  - downloadPath: optional API path to fetch the real PDF
 *  - filename: download filename
 *  - onClose: () => void
 */
export default function DocumentViewerModal({ open, title = "Documento", content, downloadPath, filename, onClose }) {
  const { t } = useTranslation();
  // While a document is open, hide the floating chat button so it never covers
  // receipt/certificate fields (e.g. the beneficiary line on mobile).
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("doc-modal-open");
    return () => document.body.classList.remove("doc-modal-open");
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-stretch md:items-start justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="pdf-viewer-modal"
    >
      <div
        className="bg-slate-100 w-full md:max-w-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col my-0 md:my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200 sticky top-0 z-10">
          <div>
            <div className="font-mono-fin text-[11px] uppercase tracking-widest text-purple-600">OpenPayments</div>
            <div className="font-display font-bold text-slate-900 text-base">{title}</div>
          </div>
          <div className="flex items-center gap-2">
            {downloadPath && (
              <button
                onClick={() => smartSaveOrShare(downloadPath, filename)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg,#7C3AED,#2563EB)" }}
                data-testid="pdf-modal-download"
              >
                <Download size={14} /> {t("documents.download_pdf")}
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="pdf-modal-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Document preview */}
        <div className="p-4 md:p-6 bg-slate-100">
          <div className="rounded-2xl shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)] bg-white" data-testid="pdf-viewer-iframe">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}
